import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import {
  Alert,
  backLinkClasses,
  Badge,
  Card,
  Icon,
  NotProvided,
  Page,
  PageHeader,
  Pagination,
  rowLinkClasses,
  Table,
  isNotFound,
  NotFoundError,
} from '@evogard/ui';
import { formatPercentage, loadFailureMessage } from '@evogard/domain';
import { MoneyValue } from '../../components/Values.js';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import {
  useListSearch,
  useSearchTerm,
  backSearch,
  useClampedPage,
} from '../../lib/list-search.js';
import { StatusBadge } from './common.js';
import { MissingRecord } from '../../components/MissingRecord.js';

/**
 * Os veículos que têm um produto — chega-se aqui pela lista de produtos.
 *
 * ## A lista é dos carros JÁ CONSULTADOS
 *
 * O produto é perguntado carro a carro, e a esteira leva dias para a primeira
 * volta da base. Um veículo que ainda não teve a vez dele não aparece aqui,
 * tenha o produto ou não — e a tela diz isso acima da tabela, porque "42
 * veículos com rastreador" seria lido como o total da praça.
 *
 * ## Duas situações na mesma linha
 *
 * A do VEÍCULO no ERP e a do PRODUTO naquele carro são coisas diferentes, e
 * cada uma tem a própria coluna: um carro ativo pode ter o produto inativo.
 */
export function ProductVehiclesScreen() {
  const { code } = useParams({ from: '/authenticated/erp/produtos/$code' });
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  /* página, tamanho, busca e filtros na URL — o "voltar" devolve a lista como estava */
  const search = useSearch({ from: '/authenticated/erp/produtos/$code' });
  const list = useListSearch(search);
  const { text, setText } = useSearchTerm(search.q, (q) => list.setFilters({ q }));
  const term = search.q ?? '';
  const { page, perPage } = list;

  const query = useQuery({
    queryKey: ['erp', 'produto-veiculos', tenantId, code, term, page, perPage],
    queryFn: async () => {
      const r = await api.erp.searchProductVehicles({
        params: { erpCode: code },
        body: { term: term.length >= 2 ? term : undefined, page, perPage },
      });
      if (r.status === 404) {
        throw new NotFoundError('Nenhum veículo já consultado tem este produto');
      }
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'os veículos deste produto'));
      }
      return r.body;
    },
    retry: false,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId && previousQuery?.queryKey[3] === code
        ? previous
        : undefined,
  });

  const result = query.data;
  /* a página além do fim (link antigo, lista que encolheu) vai para a última */
  useClampedPage(page, perPage, result && 'total' in result ? result.total : undefined);

  if (isNotFound(query.error)) {
    return (
      <MissingRecord
        title={query.error.message}
        list={{ to: '/erp/produtos', label: 'Ver os produtos' }}
      >
        Ele pode existir no ERP e ainda não ter aparecido em nenhum carro consultado. A
        consulta avança sem parar, um lote de veículos por vez.
      </MissingRecord>
    );
  }

  return (
    <Page measure="cheia">
      <Link
        to="/erp/produtos"
        search={backSearch('/erp/produtos') as never}
        className={backLinkClasses()}
      >
        Produtos
      </Link>

      <PageHeader
        title={
          result?.product.label ?? (query.isLoading ? 'Carregando…' : `Produto ${code}`)
        }
      >
        Código {code} no ERP · os veículos de {context?.currentTenant.nome} que têm este
        produto.
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {result && (
        <Alert tone="informacao">
          A lista traz só os veículos que já tiveram os produtos consultados no ERP. A
          consulta avança sem parar, um lote por vez: um veículo que ainda não foi
          consultado não aparece aqui, tenha o produto ou não.
        </Alert>
      )}

      {(!query.isError || result) && (
        <Card
          title={result === undefined ? 'Carregando…' : `${result.total} veículo(s)`}
          bleed
        >
          <Table
            paginate={false}
            search={{
              label: 'Pesquisar veículos com este produto',
              placeholder: 'Placa, veículo ou dono',
              value: text,
              onChange: setText,
            }}
            label="Veículos com este produto"
            items={result?.items ?? []}
            loading={query.isLoading}
            rowKey={(v) => v.code}
            empty={
              term
                ? 'Nenhum veículo com essa busca entre os que têm este produto.'
                : 'Nenhum veículo consultado tem este produto.'
            }
            rowLink={(v) => (
              <Link
                to="/erp/veiculos/$code"
                params={{ code: v.code }}
                aria-label={`Abrir ${v.placa ?? `o veículo ${v.code}`}`}
                className={rowLinkClasses}
              >
                <Icon nome="seta-direita" size={16} />
              </Link>
            )}
            columns={[
              {
                title: 'Placa',
                minWidth: '7rem',
                cell: (v) => v.placa ?? <NotProvided>sem placa</NotProvided>,
              },
              {
                title: 'Veículo',
                alignment: 'inicio' as const,
                minWidth: '14rem',
                cell: (v) => (
                  <span>
                    {[v.marca, v.modelo].filter(Boolean).join(' ') || (
                      <NotProvided>não informado</NotProvided>
                    )}
                    {v.anoModelo && (
                      <span className="text-texto-suave"> · {v.anoModelo}</span>
                    )}
                  </span>
                ),
              },
              {
                title: 'Dono',
                minWidth: '14rem',
                cell: (v) => v.owner?.nome ?? <NotProvided>sem associado</NotProvided>,
              },
              {
                title: 'Situação do veículo',
                minWidth: '11rem',
                cell: (v) => <StatusBadge situacao={v.situacao} />,
              },
              {
                title: 'Valor neste veículo',
                alignment: 'fim' as const,
                minWidth: '9rem',
                /* reais OU percentual: um percentual em `MoneyValue` sairia "R$ 10,00" */
                cell: (v) =>
                  v.percentage !== null ? (
                    <span className="tabular-nums">
                      {formatPercentage(v.percentage)}%
                    </span>
                  ) : (
                    <MoneyValue value={v.amount} />
                  ),
              },
              {
                title: 'Produto',
                minWidth: '7rem',
                cell: (v) =>
                  v.productStatus === null ? (
                    <NotProvided />
                  ) : (
                    <Badge tone={v.productStatus === 'ATIVO' ? 'sucesso' : 'neutro'}>
                      {v.productStatus}
                    </Badge>
                  ),
              },
            ]}
          />

          {result && (
            <Pagination
              page={result.page}
              perPage={result.perPage}
              total={result.total}
              onChange={list.setPage}
              onPerPageChange={list.setPerPage}
              label="veículos"
              /* o cartão é `bleed`; o espaçamento é de quem entra nele */
              className="border-t border-borda-suave px-5 py-4"
            />
          )}
        </Card>
      )}
    </Page>
  );
}
